import { useRoute, type RouteProp } from '@react-navigation/native';
import React, { useCallback, useEffect, useMemo } from 'react';
import { View } from 'react-native';
import { StepsForm } from '~/components';
import type { RootStackParamList } from '~/navigation/types';
import {
  AddressPage,
  ContactPage,
  IntroductionPage,
  PersonalInfoPage,
  ResumePage,
} from './components';
import useAppNavigation from '~/hooks/useAppNavigation';
import { useSalesFunnel } from '~/stores';
import type { StepContent } from '~/components/StepsForm';
import { IncomeProtectionStepNames } from '~/utils/salesFunnel';

const IncomeProtectionContract: React.FC = () => {
  const { push, navigate } = useAppNavigation();
  const { newStepLog } = useSalesFunnel();

  const { params } =
    useRoute<
      RouteProp<RootStackParamList, 'MarisaServicos_IncomeProtectionContract'>
    >();

  const currentStep = useMemo(() => {
    return Number(params?.step || 1);
  }, [params?.step]);

  const goToStep = useCallback(
    (step: number) => {
      push('MarisaServicos_IncomeProtectionContract', {
        step,
      });
    },
    [push]
  );

  const goToNextStep = useCallback(() => {
    goToStep(currentStep + 1);
  }, [currentStep, goToStep]);

  const onFinish = useCallback(() => {
    navigate('MarisaServicos_Checkout', { type: 'income-protection' });
  }, [navigate]);

  const stepsFormData = useMemo<StepContent[]>(
    () => [
      {
        step: 1,
        content: <IntroductionPage onContinue={goToNextStep} />,
        stepName: IncomeProtectionStepNames.INTRODUCTION,
      },
      {
        step: 2,
        content: <PersonalInfoPage onContinue={goToNextStep} />,
        stepName: IncomeProtectionStepNames.PERSONAL_INFO,
      },
      {
        step: 3,
        content: <ContactPage onContinue={goToNextStep} />,
        stepName: IncomeProtectionStepNames.CONTACT,
      },
      {
        step: 4,
        content: <AddressPage onContinue={goToNextStep} />,
        stepName: IncomeProtectionStepNames.ADDRESS,
      },
      {
        step: 5,
        content: <ResumePage onContinue={onFinish} />,
        stepName: IncomeProtectionStepNames.RESUME,
      },
    ],
    [goToNextStep, onFinish]
  );

  const currentStepFormData = useMemo(
    () => stepsFormData.find((data) => data.step === currentStep),
    [currentStep, stepsFormData]
  );

  useEffect(() => {
    if (currentStepFormData?.stepName) {
      newStepLog(currentStepFormData.stepName);
    }
  }, [currentStepFormData?.stepName, newStepLog]);

  return (
    <View className="flex-1 bg-light-100">
      <StepsForm data={stepsFormData} currentStep={currentStep} />
    </View>
  );
};

export default IncomeProtectionContract;
